import type { ErrorInfo, ReactNode } from 'react';
import React, { Component } from 'react';

interface Props {
  widgetId: string;
  widgetName?: string;
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class WidgetErrorBoundary extends Component<Props, State> {
  private retryCount = 0;
  private maxRetries = 3;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error for debugging
    console.error(`Widget Error [${this.props.widgetId}]:`, error, errorInfo);

    // Call onError callback if provided
    this.props.onError?.(error, errorInfo);

    // NOTE: Error monitoring service integration deferred to Epic 18 (Advanced Features)
    // When implemented, integrate with Sentry, LogRocket, or similar service here
  }

  private handleRetry = () => {
    if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
    }
  };

  private handleReset = () => {
    this.retryCount = 0;
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="widget bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-4">
          <div className="flex flex-col items-center justify-center h-full text-center min-h-[200px]">
            {/* Error Icon */}
            <div className="mb-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-full">
                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5C3.312 17.333 4.271 19 5.812 19z" />
                </svg>
              </div>
            </div>

            {/* Error Message */}
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Widget Error
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
              {this.props.widgetName || this.props.widgetId} failed to load
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              {this.retryCount < this.maxRetries && (
                <button
                  onClick={this.handleRetry}
                  className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors"
                >
                  Retry ({this.maxRetries - this.retryCount} left)
                </button>
              )}
              <button
                onClick={this.handleReset}
                className="px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
              >
                Reset
              </button>
            </div>

            {/* Error Details (Development Mode) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left w-full">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
                  Show Error Details
                </summary>
                <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-700 dark:text-gray-300 overflow-auto max-h-32">
                  <div className="font-mono">
                    <div><strong>Error:</strong> {this.state.error.message}</div>
                    {this.state.error.stack && (
                      <div className="mt-1">
                        <strong>Stack:</strong>
                        <pre className="mt-1 whitespace-pre-wrap">{this.state.error.stack}</pre>
                      </div>
                    )}
                    {this.state.errorInfo?.componentStack && (
                      <div className="mt-1">
                        <strong>Component Stack:</strong>
                        <pre className="mt-1 whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                      </div>
                    )}
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// HOC for easier widget wrapping
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  widgetId: string,
  widgetName?: string
) => {
  const WrappedComponent = (props: P) => (
    <WidgetErrorBoundary widgetId={widgetId} widgetName={widgetName}>
      <Component {...props} />
    </WidgetErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;
  
  return WrappedComponent;
};